if (!customElements.get('media-card-carousel')) {
  class MediaCardCarousel extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedOnce) return;
      this.isConnectedOnce = true;

      this.sliderElement = this.querySelector('[data-media-card-slider]');
      this.controlsElement = this.querySelector('[data-media-card-controls]');
      this.indicatorElement = this.querySelector('[data-media-card-indicator]');
      this.prevButton = this.querySelector('[data-media-card-prev]');
      this.nextButton = this.querySelector('[data-media-card-next]');
      this.indicatorCount = 0;
      this.handleClickBound = this.handleClick.bind(this);
      this.handleWindowLoadBound = this.initSwiper.bind(this);
      this.handleModalInteractionBound = this.handleModalInteraction.bind(this);
      this.handleModalChangeBound = this.handleModalChange.bind(this);

      this.addEventListener('click', this.handleClickBound);
      this.syncVideoTriggers();
      this.setupModals();

      if (typeof window.Swiper === 'undefined') {
        window.addEventListener('load', this.handleWindowLoadBound, { once: true });
      } else {
        this.initSwiper();
      }

      if (typeof ResizeObserver !== 'undefined' && this.sliderElement) {
        this.resizeObserver = new ResizeObserver(() => this.swiper?.update());
        this.resizeObserver.observe(this.sliderElement);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClickBound);
      window.removeEventListener('load', this.handleWindowLoadBound);
      this.resizeObserver?.disconnect();
      this.modalObserver?.disconnect();
      this.swiper?.destroy(true, true);
      this.cleanupModal(false);
      this.modalElement?.removeEventListener('click', this.handleModalInteractionBound);
      this.modalElement?.removeEventListener('keyup', this.handleModalInteractionBound);
      this.modalElement?.remove();
      this.swiper = null;
      this.modalElement = null;
      this.modalBody = null;
      this.isConnectedOnce = false;
    }

    initSwiper() {
      if (this.swiper || !this.sliderElement || typeof window.Swiper === 'undefined') return;

      const desktopCards = Number.parseFloat(this.dataset.desktopCards) || 3;
      const mobileCards = Number.parseFloat(this.dataset.mobileCards) || 1;

      this.swiper = new window.Swiper(this.sliderElement, {
        slidesPerView: mobileCards,
        slidesPerGroup: 1,
        spaceBetween: 15,
        loop: false,
        grabCursor: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        a11y: {
          enabled: true,
        },
        breakpoints: {
          750: {
            slidesPerView: desktopCards,
            spaceBetween: 20,
          },
        },
        on: {
          init: (swiper) => this.syncControls(swiper),
          update: (swiper) => this.syncControls(swiper),
          resize: (swiper) => this.syncControls(swiper),
          breakpoint: (swiper) => this.syncControls(swiper),
          slideChange: (swiper) => this.syncControls(swiper),
          lock: (swiper) => this.syncControls(swiper),
          unlock: (swiper) => this.syncControls(swiper),
        },
      });

      this.syncControls(this.swiper);
    }

    handleClick(event) {
      const videoTrigger = event.target.closest('[data-media-card-video-trigger]');
      if (videoTrigger && this.contains(videoTrigger)) {
        this.openVideo(videoTrigger);
        return;
      }

      const indicator = event.target.closest('[data-media-card-indicator-index]');
      if (indicator && this.contains(indicator) && this.swiper) {
        const index = Number.parseInt(indicator.dataset.mediaCardIndicatorIndex, 10);
        if (!Number.isNaN(index)) this.swiper.slideTo(index);
        return;
      }

      const prevButton = event.target.closest('[data-media-card-prev]');
      if (prevButton && this.contains(prevButton) && this.swiper) {
        this.swiper.slidePrev();
        return;
      }

      const nextButton = event.target.closest('[data-media-card-next]');
      if (nextButton && this.contains(nextButton) && this.swiper) {
        this.swiper.slideNext();
      }
    }

    syncVideoTriggers() {
      this.querySelectorAll('[data-media-card]').forEach((card) => {
        const hasVideo = card.dataset.hasVideo === 'true';
        card.querySelectorAll('[data-media-card-video-trigger]').forEach((trigger) => {
          trigger.disabled = !hasVideo;
          trigger.setAttribute('aria-disabled', String(!hasVideo));
        });
      });
    }

    openVideo(trigger) {
      if (trigger.disabled) return;

      const card = trigger.closest('[data-media-card]');
      const template = card?.querySelector('[data-media-card-video-template]');
      if (!template || !this.modalElement || !this.modalBody) return;

      if (typeof this.modalElement.show !== 'function') return;

      this.modalBody.replaceChildren(template.content.cloneNode(true));
      this.activeTrigger = trigger;
      this.modalElement.show(trigger);

      window.requestAnimationFrame(() => {
        this.modalElement.querySelector('[role="dialog"]')?.focus();
      });

      const video = this.modalBody.querySelector('video');
      video?.play().catch(() => {});
    }

    setupModals() {
      this.modalElement = document.querySelector(this.dataset.modalId);
      this.modalBody = this.modalElement?.querySelector('[data-media-card-modal-body]');

      if (!this.modalElement) return;

      this.modalElement.addEventListener('click', this.handleModalInteractionBound);
      this.modalElement.addEventListener('keyup', this.handleModalInteractionBound);

      if (typeof MutationObserver !== 'undefined') {
        this.modalObserver = new MutationObserver(this.handleModalChangeBound);
        this.modalObserver.observe(this.modalElement, {
          attributes: true,
          attributeFilter: ['open'],
        });
      }
    }

    handleModalChange() {
      if (!this.modalElement?.hasAttribute('open')) {
        this.cleanupModal(true);
      }
    }

    cleanupModal(restoreFocus) {
      this.modalBody?.querySelectorAll('video').forEach((video) => video.pause());
      this.modalBody?.replaceChildren();

      if (restoreFocus) {
        const target = this.activeTrigger || this.modalElement?.openedBy;
        target?.focus();
      }

      this.activeTrigger = null;
    }

    handleModalInteraction(event) {
      if (!event.currentTarget.hasAttribute('open')) {
        this.cleanupModal(true);
      }
    }

    syncControls(swiper = this.swiper) {
      if (!swiper || !this.controlsElement || !this.indicatorElement) return;

      const snapCount = swiper.snapGrid?.length || 0;
      const slideCount = swiper.slides?.length || 0;
      const slidesPerView = Number.parseFloat(swiper.params.slidesPerView) || 1;
      const visibleCardCount = Math.max(1, Math.round(slidesPerView));
      const indicatorCount = Math.min(
        snapCount,
        Math.max(slideCount - visibleCardCount + 1, 1),
      );
      const activeIndicatorIndex = Math.min(swiper.snapIndex || 0, indicatorCount - 1);
      const isLocked = swiper.isLocked || indicatorCount <= 1;

      this.classList.toggle('is-locked', isLocked);
      this.controlsElement.hidden = isLocked;

      if (isLocked) return;

      this.renderIndicators(indicatorCount, activeIndicatorIndex, snapCount);
      this.updateNavigation(swiper);
    }

    renderIndicators(count, activeIndex, snapCount) {
      if (this.indicatorCount !== count) {
        this.indicatorElement.replaceChildren();

        const fragment = document.createDocumentFragment();
        for (let index = 0; index < count; index += 1) {
          const button = document.createElement('button');
          const targetIndex = index === count - 1 ? snapCount - 1 : index;

          button.type = 'button';
          button.className = 'media-card-carousel__indicator-item';
          button.dataset.mediaCardIndicatorIndex = String(targetIndex);
          button.setAttribute('aria-label', `Go to carousel position ${index + 1}`);
          fragment.appendChild(button);
        }

        this.indicatorElement.appendChild(fragment);
        this.indicatorCount = count;
      }

      this.indicatorElement
        .querySelectorAll('[data-media-card-indicator-index]')
        .forEach((button, index) => {
          const isActive = index === activeIndex;
          const targetIndex = index === count - 1 ? snapCount - 1 : index;

          button.dataset.mediaCardIndicatorIndex = String(targetIndex);
          button.classList.toggle('is-active', isActive);

          if (isActive) {
            button.setAttribute('aria-current', 'true');
          } else {
            button.removeAttribute('aria-current');
          }
        });
    }

    updateNavigation(swiper) {
      if (this.prevButton) {
        this.prevButton.disabled = swiper.isBeginning;
        this.prevButton.setAttribute('aria-disabled', String(swiper.isBeginning));
      }

      if (this.nextButton) {
        this.nextButton.disabled = swiper.isEnd;
        this.nextButton.setAttribute('aria-disabled', String(swiper.isEnd));
      }
    }
  }
  customElements.define('media-card-carousel', MediaCardCarousel);
}
